import { config } from '../utils/config.js';

export class OrderService {
  constructor(configManager) {
    this.baseUrl = `${config.getApiUrl()}/api/v1`;
    this.configManager = configManager;
    try {
      this.authToken = config.getAuthToken();
    } catch (e) {
      console.warn('[OrderService] No auth token available, will fail on API calls:', e.message);
      this.authToken = null;
    }
  }

  async createOrder(orderData) {
    try {
      const restaurantId = this.configManager.getCurrentRestaurantId();
      if (!restaurantId) {
        throw new Error('No restaurant selected');
      }

      console.log('Creating order:', {
        restaurantId,
        orderData,
        url: `${this.baseUrl}/device/orders`
      });

      const response = await fetch(`${this.baseUrl}/device/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${this.authToken}`,
        },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: errorText };
        }

        console.error('Order creation failed:', {
          status: response.status,
          statusText: response.statusText,
          restaurantId,
          orderData,
          errorData
        });

        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  }

  /**
   * Transform cart items to the API format
   */
  transformCartItemsToOrderItems(cartItems, menuData) {
    return cartItems.map(cartItem => {
      const originalItem = this.findMenuItemById(cartItem.id, menuData);

      const orderItem = {
        menuItemId: originalItem?.id || cartItem.id,
        name: cartItem.name,
        quantity: cartItem.quantity,
        unitPrice: cartItem.price,
        modifiers: this.transformModifiers(cartItem.modifiers || {}),
      };

      if (cartItem.specialInstructions) {
        orderItem.specialInstructions = cartItem.specialInstructions;
      }

      if (originalItem?.sku) {
        orderItem.productSku = originalItem.sku;
      }

      if (originalItem?.categoryId) {
        orderItem.categoryId = originalItem.categoryId;
      }

      if (originalItem?.prepTimeMinutes) {
        orderItem.prepTimeMinutes = originalItem.prepTimeMinutes;
      }

      return orderItem;
    });
  }

  /**
   * Transform modifiers object to API format
   */
  transformModifiers(modifiers) {
    if (!modifiers || typeof modifiers !== 'object') {
      return [];
    }

    return Object.entries(modifiers).map(([id, value]) => ({
      id,
      name: value,
      priceAdjustment: 0,
      category: 'custom'
    }));
  }

  /**
   * Find menu item by ID in the menu data structure
   */
  findMenuItemById(itemId, menuData) {
    if (!menuData || !menuData.items) return null;

    for (const categoryItems of Object.values(menuData.items)) {
      const item = categoryItems.find(item => item.id === itemId);
      if (item) return item;
    }
    return null;
  }

  /**
   * Create order payload from cart data
   */
  createOrderPayload(cartItems, totals, menuData, orderOptions = {}) {
    const transformedItems = this.transformCartItemsToOrderItems(cartItems, menuData);

    const payload = {
      items: transformedItems,
      type: orderOptions.type || 'dine-in',
    };

    if (orderOptions.customerInfo && Object.keys(orderOptions.customerInfo).length > 0) {
      payload.customerInfo = orderOptions.customerInfo;
    }

    if (orderOptions.deviceId) {
      payload.deviceId = orderOptions.deviceId;
    }

    if (orderOptions.tableNumber) {
      payload.tableNumber = orderOptions.tableNumber;
    }

    if (orderOptions.zoneId) {
      payload.zoneId = orderOptions.zoneId;
    }

    if (orderOptions.specialInstructions) {
      payload.specialInstructions = orderOptions.specialInstructions;
    }

    if (orderOptions.paymentProvider) {
      payload.paymentProvider = orderOptions.paymentProvider;
    }

    return payload;
  }

  async getUnpaidOrders() {
    try {
      const restaurantId = this.configManager.getCurrentRestaurantId();
      const response = await fetch(`${this.baseUrl}/device/orders/unpaid`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${this.authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch unpaid orders:', error);
      return [];
    }
  }
}
